import React from 'react'
import * as appSelectors from 'app/state/app/app-selectors';
import * as cs from 'common/common-styles';
import { FormikError, ReverseArgumentInput } from 'configurable-form/components/inputs/shared-components';
import { useInput } from 'configurable-form/components/utils/use-input';
import { withVisibility } from 'configurable-form/components/utils/with-visibility';
import debounce from 'lodash/debounce';
import set from 'lodash/set';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputGroup, Loader } from 'rsuite';
import styled from 'styled-components';
import { captureExceptionWithContext } from 'utils/sentry-functions';
import { usePropertyLookup } from '../hooks/usePropertyLookup';
import { BasicError } from './basic-error';
import ParcelLookup from './parcel-lookup';
import { formatErrorMessage, shouldQuery } from './shared';

const ConstrainedInputGroup = styled(InputGroup)`
  width: 400px !important; // Matches the width of "ReverseArgumentInput"
`;

function ParcelNumberInputFieldComponent(props) {
  const [isQuerying, setIsQuerying] = useState(false);

  const cityInfo = useSelector(appSelectors.cityInfo);

  const { field, meta } = useInput({ name: props.name, validation: props.validation });

  const [serverErrorMessage, setServerErrorMessage] = useState(undefined);

  const {
    clearStoredStates,
    queryForMatchingProperty,
  } = usePropertyLookup();

  const getPropertyFromAPN = useMemo(() => debounce(
    async (e) => {
      setIsQuerying(true);

      const apn = String(e.target.value).trim();

      try {
        await queryForMatchingProperty({ apn });
        setServerErrorMessage(undefined);
      } catch (err) {
        captureExceptionWithContext(err, { parcelNumber: apn });

        const lookupErrorMessage = formatErrorMessage(
          err?.response?.data?.Message,
          props?.additionalErrorMessageText
        );

        if (lookupErrorMessage) setServerErrorMessage(lookupErrorMessage);
      } finally {
        setIsQuerying(false);
      }
    }, 300
  ), [props?.additionalErrorMessageText, queryForMatchingProperty]);

  const onChange = useCallback((e) => {
    if (serverErrorMessage) setServerErrorMessage(undefined);

    clearStoredStates();
    set(e, 'target.value', String(e.target.value).replaceAll(/[\W_]+/g, ''));
    field.onChange(e);

    if (shouldQuery(e)) getPropertyFromAPN(e);
  }, [clearStoredStates, field, serverErrorMessage, getPropertyFromAPN]);

  return (
    <div>
      <cs.InputTextFieldLabel>{props.label}</cs.InputTextFieldLabel>

      <ConstrainedInputGroup inside>
        <ReverseArgumentInput {...field} value={field.value || ''} onChange={onChange} autoComplete="off" />
        {isQuerying && (<InputGroup.Addon><Loader /></InputGroup.Addon>)}
      </ConstrainedInputGroup>

      <FormikError {...meta} />

      {/* Error message returned to the client to render by the server */}
      <BasicError message={serverErrorMessage} />

      {/* Note that this information comes from "cityInfo" instead of the "PropertyLookup" props themselves */}
      <ParcelLookup parcelLookupWebsite={cityInfo?.parcelLookupWebsite}
        parcelLookupLinkText={cityInfo?.parcelLookupLinkText}
        parcelLookupLinkHtml={cityInfo?.parcelLookupLinkHtml}
      />
    </div>
  )
}

export const ParcelNumberInputField = withVisibility(ParcelNumberInputFieldComponent);
